/**
 * `/agents/claims` — Phase 3.G claims/lanes operator view (#46036).
 *
 * Who holds what, for how long, and which lanes are alive. Claim age and
 * liveness, not just assignee — stale claims, zombie worktrees, watchdog-
 * reaped lanes, and dead-holder processes.
 */

import { createRoute } from "@tanstack/react-router";
import { RouterLink as Link, useSearch } from "@excellent/ui/router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  IntentBadge,
  PageHeader,
  StatStrip,
  formatRelativeTime,
  type DataTableColumn,
  type StatStripItem,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import {
  claimsLanes,
  type ClaimRowDto,
  type ClaimsLanesViewDto,
  type LaneRowDto,
} from "@excellent/ui/api/contracts/claims-lanes";
import { rootRoute } from "./__root";

const NUM = "tabular-nums";
const MUTED = "type-subtext text-muted-foreground";

function liveIntent(liveness: ClaimRowDto["liveness"]): "success" | "danger" | "warning" | "neutral" {
  if (liveness === "pid-alive") return "success";
  if (liveness === "confirmed-dead" || liveness === "unresponsive") return "danger";
  if (liveness === "no-lease") return "warning";
  return "neutral";
}

function liveLabel(liveness: ClaimRowDto["liveness"]): string {
  if (liveness === "pid-alive") return "alive";
  if (liveness === "confirmed-dead") return "dead holder";
  if (liveness === "unresponsive") return "unresponsive";
  return "no lease";
}

function AgentsClaimsPage() {
  const { data, error, isLoading } = useApi(claimsLanes.get, {});
  const searchParams = useSearch();
  const q = (searchParams.get("q") ?? "").trim().toLowerCase();

  const oracleError = error
    ? error instanceof Error
      ? error.message
      : String(error)
    : data && !data.ok
      ? data.error
      : null;

  return (
    <div className="px-6 py-8 space-y-6">
      <PageHeader
        title="Claims & lanes"
        description="Who holds what, for how long, and which lanes are alive — claim age and liveness, not just assignee."
        search="Search claims"
      />

      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Reading claims and worktree lanes…</p>
      ) : oracleError !== null || !data?.ok ? (
        <Card>
          <CardHeader>
            <CardTitle>Claims census unavailable</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="type-body text-muted-foreground">
              Could not assemble the claims/lanes payload. The vault or MCP CLI
              may be unavailable.
            </p>
            <p className={`${MUTED} mt-2 break-words`}>{oracleError}</p>
          </CardContent>
        </Card>
      ) : (
        <ClaimsLanesBody status={data.status} query={q} />
      )}
    </div>
  );
}

function ClaimsLanesBody({ status, query }: { status: ClaimsLanesViewDto; query: string }) {
  const claims = query
    ? status.claims.filter(
        (c) =>
          String(c.taskId).includes(query) ||
          c.title.toLowerCase().includes(query) ||
          (c.holder ?? "").toLowerCase().includes(query) ||
          (c.laneKey ?? "").toLowerCase().includes(query),
      )
    : status.claims;
  const lanes = query
    ? status.lanes.filter(
        (l) =>
          l.key.toLowerCase().includes(query) ||
          l.branch.toLowerCase().includes(query) ||
          l.path.toLowerCase().includes(query) ||
          String(l.taskId ?? "").includes(query),
      )
    : status.lanes;

  const deadHolders = status.claims.filter((c) => c.liveness === "confirmed-dead").length;
  const zombies = status.lanes.filter((l) => l.kind === "zombie").length;
  const reaped = status.gcLast?.removed.length ?? 0;

  const headline: StatStripItem[] = [
    {
      title: "Open claims",
      value: String(status.claims.length),
      intent: status.claims.length === 0 ? "neutral" : "info",
      description: `${deadHolders} dead holder${deadHolders === 1 ? "" : "s"}`,
    },
    {
      title: "Lanes",
      value: status.pool ? `${status.pool.alive} / ${status.pool.size}` : String(status.lanes.length),
      intent: status.pool && status.pool.dead > 0 ? "danger" : "neutral",
      description: status.pool
        ? `${status.pool.leased} leased · ${status.pool.free} free · ${status.pool.dead} dead`
        : "pool unread",
    },
    {
      title: "Zombies",
      value: String(zombies),
      intent: zombies === 0 ? "success" : "danger",
      description: "pool dirs that are not git worktrees",
    },
    {
      title: "Watchdog GC",
      value: String(reaped),
      intent: reaped === 0 ? "neutral" : "warning",
      description: status.gcLast?.at
        ? `last sweep ${formatRelativeTime(status.gcLast.at)}`
        : "no sweep recorded",
    },
  ];

  const claimCols: DataTableColumn<ClaimRowDto>[] = [
    {
      key: "taskId",
      header: "Task",
      sort: (a, b) => a.taskId - b.taskId,
      render: (r) => (
        <Link href={`/issues?task=${r.taskId}`} className={`${NUM} font-medium text-foreground hover:underline`}>
          #{r.taskId}
        </Link>
      ),
    },
    {
      key: "title",
      header: "Title",
      sort: (a, b) => a.title.localeCompare(b.title),
      render: (r) => (
        <span className="block max-w-[48ch] truncate text-foreground" title={r.title}>
          {r.title}
        </span>
      ),
    },
    {
      key: "holder",
      header: "Holder",
      sort: (a, b) => (a.holder ?? "").localeCompare(b.holder ?? ""),
      render: (r) => <span className="font-mono type-subtext text-foreground">{r.holder ?? "—"}</span>,
    },
    {
      key: "liveness",
      header: "Liveness",
      sort: (a, b) => a.liveness.localeCompare(b.liveness),
      render: (r) => (
        <IntentBadge intent={liveIntent(r.liveness)} withDot>
          {liveLabel(r.liveness)}
        </IntentBadge>
      ),
    },
    {
      key: "quietKind",
      header: "Quiet",
      render: (r) =>
        r.quietKind ? (
          <span className={MUTED}>{r.quietKind}</span>
        ) : (
          <span className={MUTED}>—</span>
        ),
    },
    {
      key: "claimedAt",
      header: "Claimed",
      sort: (a, b) => (a.claimedAt ?? "").localeCompare(b.claimedAt ?? ""),
      render: (r) => (
        <span className={MUTED}>{r.claimedAt ? formatRelativeTime(r.claimedAt) : "—"}</span>
      ),
    },
    {
      key: "updatedAt",
      header: "Updated",
      sort: (a, b) => a.updatedAt.localeCompare(b.updatedAt),
      render: (r) => <span className={MUTED}>{formatRelativeTime(r.updatedAt)}</span>,
    },
    {
      key: "laneKey",
      header: "Lane",
      render: (r) => <span className={`${MUTED} font-mono`}>{r.laneKey ?? "—"}</span>,
    },
    {
      key: "pid",
      header: "Pid",
      render: (r) => <span className={`${NUM} ${MUTED}`}>{r.pid ?? "—"}</span>,
    },
  ];

  const laneCols: DataTableColumn<LaneRowDto>[] = [
    {
      key: "key",
      header: "Lane",
      sort: (a, b) => a.key.localeCompare(b.key),
      render: (r) => <span className="font-mono type-subtext text-foreground">{r.key}</span>,
    },
    {
      key: "kind",
      header: "Kind",
      sort: (a, b) => a.kind.localeCompare(b.kind),
      render: (r) => (
        <IntentBadge intent={r.kind === "zombie" ? "danger" : "neutral"} withDot>
          {r.kind}
        </IntentBadge>
      ),
    },
    {
      key: "leased",
      header: "Lease",
      render: (r) =>
        r.leased ? (
          <IntentBadge intent={r.pidAlive === false ? "danger" : "success"} withDot>
            {r.pidAlive === false ? "dead pid" : "held"}
          </IntentBadge>
        ) : (
          <span className={MUTED}>free</span>
        ),
    },
    {
      key: "taskId",
      header: "Task",
      render: (r) =>
        r.taskId != null ? (
          <Link href={`/issues?task=${r.taskId}`} className={`${NUM} text-foreground hover:underline`}>
            #{r.taskId}
          </Link>
        ) : (
          <span className={MUTED}>—</span>
        ),
    },
    {
      key: "branch",
      header: "Branch",
      render: (r) => (
        <span className={`${MUTED} font-mono block max-w-[36ch] truncate`} title={r.branch}>
          {r.branch || "—"}
        </span>
      ),
    },
    {
      key: "dirty",
      header: "Tree",
      render: (r) => <span className={MUTED}>{r.dirty ? "dirty" : "clean"}</span>,
    },
    {
      key: "pid",
      header: "Pid",
      render: (r) => <span className={`${NUM} ${MUTED}`}>{r.pid ?? "—"}</span>,
    },
  ];

  return (
    <>
      <StatStrip items={headline} />

      <DataTable
        title={`${claims.length} claim${claims.length === 1 ? "" : "s"}`}
        subtitle="in_progress rows with claim age and holder liveness from claim_leases"
        rows={claims}
        columns={claimCols}
        rowKey={(r) => r.taskId}
        empty="No in_progress claims in this vault."
      />

      <DataTable
        title={`${lanes.length} lane${lanes.length === 1 ? "" : "s"}`}
        subtitle="excellent-mcp worktree list census — zombies and dead pids stay visible"
        rows={lanes}
        columns={laneCols}
        rowKey={(r) => r.key}
        empty="No managed worktree lanes on this node."
      />

      {status.gcLast && status.gcLast.removed.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Watchdog-reaped lanes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className={MUTED}>
              Last GC {status.gcLast.at ? formatRelativeTime(status.gcLast.at) : "unrecorded"}{" "}
              removed {status.gcLast.removed.length} tree
              {status.gcLast.removed.length === 1 ? "" : "s"}
              {status.gcLast.flaggedDirty.length
                ? ` · ${status.gcLast.flaggedDirty.length} dirty preserved`
                : ""}
              .
            </p>
            <ul className={`${MUTED} mt-2 list-disc space-y-1 pl-4 font-mono`}>
              {status.gcLast.removed.map((k) => (
                <li key={k}>{k}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {status.notes.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className={`${MUTED} list-disc space-y-1 pl-4`}>
              {status.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </>
  );
}

export const agentsClaimsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/claims",
  component: AgentsClaimsPage,
});
